import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";

// This object reads the variables from your .env file
// CRITICAL: Never construct .web.app URLs - always use firebaseapp.com domain
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'demo-api-key',
  // Ensure authDomain never uses .web.app - always use .firebaseapp.com
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'yoursongrequest-fe6e9.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'yoursongrequest-fe6e9',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'yoursongrequest-fe6e9.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '80278120867',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:80278120867:web:12b0293d4a6806e7bf5216',
};

// CRITICAL: Sanitize authDomain to prevent mixed content errors
// Firebase authDomain should NEVER include protocol (http:// or https://)
// and should NEVER use .web.app domain
if (firebaseConfig.authDomain) {
  let sanitized = firebaseConfig.authDomain;
  
  // Remove any protocol (http:// or https://) - authDomain should be domain only
  sanitized = sanitized.replace(/^https?:\/\//i, '');
  
  // Remove trailing slashes
  sanitized = sanitized.replace(/\/+$/, '');
  
  // Replace .web.app with .firebaseapp.com to prevent mixed content errors
  if (sanitized.includes('.web.app')) {
    console.warn('⚠️ WARNING: authDomain contains .web.app - replacing with .firebaseapp.com');
    sanitized = sanitized.replace('.web.app', '.firebaseapp.com');
  }
  
  // Ensure we're using the custom domain if available, otherwise firebaseapp.com
  // Prefer yoursongrequest.com or yoursongrequest-fe6e9.firebaseapp.com
  if (sanitized.includes('yoursongrequest-fe6e9.web.app')) {
    console.warn('⚠️ WARNING: authDomain contains insecure .web.app - forcing .firebaseapp.com');
    sanitized = 'yoursongrequest-fe6e9.firebaseapp.com';
  }
  
  firebaseConfig.authDomain = sanitized;
  
  console.log('🔒 Sanitized authDomain:', firebaseConfig.authDomain);
}

// Debug logging
console.log('🔧 Firebase Config:', {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ? '✅ Set' : '❌ Missing',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ? '✅ Set' : '❌ Missing',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ? '✅ Set' : '❌ Missing',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ? '✅ Set' : '❌ Missing',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ? '✅ Set' : '❌ Missing',
  appId: import.meta.env.VITE_FIREBASE_APP_ID ? '✅ Set' : '❌ Missing',
});

// Initialize Firebase
let app, db, auth, storage, functions;

try {
  app = initializeApp(firebaseConfig);
  console.log('✅ Firebase initialized successfully');
  
  db = getFirestore(app);
  auth = getAuth(app);
  storage = getStorage(app);
  // Initialize functions with the same app instance (ensures auth context is shared)
  functions = getFunctions(app, 'us-central1'); // Specify region explicitly
  
  // Mobile-specific configuration
  if (typeof window !== 'undefined') {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    console.log('🔍 DEBUG: Mobile detection:', { isMobile });
    
    if (isMobile) {
      console.log('🔍 DEBUG: Mobile environment detected, applying specific configurations');
      
      // Force long polling for mobile to avoid WebSocket issues
      if (db) {
        try {
          // Use dynamic import instead of require for ES modules
          import('firebase/firestore').then(({ enableNetwork }) => {
            // Enable network to ensure connection
            enableNetwork(db).then(() => {
              console.log('✅ Firestore network enabled for mobile');
            }).catch(error => {
              console.warn('⚠️ Could not enable Firestore network:', error);
            });
          }).catch(error => {
            console.warn('⚠️ Could not import Firestore functions:', error);
          });
        } catch (error) {
          console.warn('⚠️ Could not apply mobile Firestore configuration:', error);
        }
      }
    }
  }
  
  console.log('✅ Firebase services initialized:', { db: !!db, auth: !!auth, storage: !!storage, functions: !!functions });
  
  // Connect to Firebase Emulators in development mode
  if (import.meta.env.DEV && typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    console.log('🔧 Local development detected - connecting to Firebase Emulators');
    
    // Connect Firestore to emulator
    import('firebase/firestore').then(({ connectFirestoreEmulator }) => {
      if (db) {
        try {
          connectFirestoreEmulator(db, 'localhost', 8080);
          console.log('✅ Firestore connected to emulator on port 8080');
        } catch (error) {
          // Emulator already connected, ignore error
          if (!error.message.includes('already been initialized')) {
            console.warn('⚠️ Firestore emulator connection warning:', error.message);
          }
        }
      }
    }).catch(error => {
      console.warn('⚠️ Could not connect Firestore to emulator:', error);
    });
    
    // Connect Auth to emulator
    import('firebase/auth').then(({ connectAuthEmulator }) => {
      if (auth) {
        try {
          connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
          console.log('✅ Auth connected to emulator on port 9099');
        } catch (error) {
          // Emulator already connected, ignore error
          if (!error.message.includes('already been initialized')) {
            console.warn('⚠️ Auth emulator connection warning:', error.message);
          }
        }
      }
    }).catch(error => {
      console.warn('⚠️ Could not connect Auth to emulator:', error);
    });
    
    // DISABLED: Connect Functions to emulator
    // Using deployed (live) Cloud Functions instead so real Auth tokens work
    // and we can access real Stripe secrets
    // import('firebase/functions').then(({ connectFunctionsEmulator }) => {
    //   if (functions) {
    //     try {
    //       connectFunctionsEmulator(functions, 'localhost', 5001);
    //       console.log('✅ Functions connected to emulator on port 5001');
    //     } catch (error) {
    //       // Emulator already connected, ignore error
    //       if (!error.message.includes('already been initialized')) {
    //         console.warn('⚠️ Functions emulator connection warning:', error.message);
    //       }
    //     }
    //   }
    // }).catch(error => {
    //   console.warn('⚠️ Could not connect Functions to emulator:', error);
    // });
    console.log('✅ Using deployed (live) Cloud Functions - emulator disabled');
  }
  
  // Test Firestore connection
  if (db) {
    console.log('🔍 DEBUG: Firestore instance created:', db);
    console.log('🔍 DEBUG: Firestore app:', db.app);
    console.log('🔍 DEBUG: Firestore settings:', db.settings);
  }
  
  // Test Auth connection
  if (auth) {
    console.log('🔍 DEBUG: Auth instance created:', auth);
    console.log('🔍 DEBUG: Auth app:', auth.app);
    console.log('🔍 DEBUG: Auth current user:', auth.currentUser);
  }
} catch (error) {
  console.error('❌ Firebase initialization failed:', error);
  console.error('❌ Firebase error details:', {
    code: error.code,
    message: error.message,
    stack: error.stack
  });
  // Don't throw error, just log it and continue
  console.warn('⚠️ Continuing without Firebase services');
}

// Export 'db', 'auth', 'storage', 'functions', and 'app' so we can use them in other parts of our application
// Provide fallback values in case initialization fails
export { db, auth, storage, functions, app };